// FILE: src/Pricing.jsx
import React from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  createCheckoutSession,
  createPortalLink,
  getCatalog,
  getCustomerContext,
  updateSubscription,
} from "./lib/billingApi";
import Menu from "./Menu";
import { appAlert } from "./components/AppDialog";

const currencyFromLocale = () => {
  const l = (navigator.language || "en-GB").toLowerCase();
  if (l.includes("gb") || l.startsWith("en-uk")) return "gbp";
  if (l.includes("us")) return "usd";
  if (l.includes("au")) return "aud";
  if (l.includes("nz")) return "nzd";
  if (l.includes("ca")) return "cad";
  // Soft launch: GBP-first catalog
  return "gbp";
};

const fmtMoney = (amount, currency) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "gbp").toUpperCase() })
    .format((amount || 0) / 100);

export default function Pricing() {
  const auth = React.useMemo(() => getAuth(), []);
  const [user, setUser] = React.useState(null);
  const [loadingAuth, setLoadingAuth] = React.useState(true);

  const [showMenu, setShowMenu] = React.useState(false);

  // Catalog from Stripe
  const [catalog, setCatalog] = React.useState([]);
  const [currency, setCurrency] = React.useState(currencyFromLocale());

  // Customer context
  const [customerCurrency, setCustomerCurrency] = React.useState(null);
  const [activeSub, setActiveSub] = React.useState(null);

  // Quantity for subscribe/update
  const [qty, setQty] = React.useState(1);

  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setLoadingAuth(false);
    });
    return () => unsub();
  }, [auth]);

  // Load catalog
  React.useEffect(() => {
    (async () => {
      try {
        const cat = await getCatalog();
        setCatalog(cat.items || []);
      } catch (e) {
        console.error("Failed to load catalog", e);
      }
    })();
  }, []);

  // Load customer context (default currency + current sub)
  React.useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const ctx = await getCustomerContext();
        if (ctx.defaultCurrency) setCustomerCurrency(ctx.defaultCurrency);
        if (ctx.subscription) {
          setActiveSub(ctx.subscription);
          const q = ctx.subscription.items?.[0]?.quantity || 1;
          setQty(q);
        }
      } catch (e) {
        console.error("Failed to load customer context", e);
      }
    })();
  }, [user]);

  // If Stripe says the user tends to pay in X, prefer that
  React.useEffect(() => {
    if (customerCurrency) setCurrency(customerCurrency);
  }, [customerCurrency]);

  // Available currencies in your catalog
  const catalogCurrencies = React.useMemo(
    () => Array.from(new Set(catalog.map((p) => p.currency))).sort(),
    [catalog]
  );

  // Weekly subscription prices in the selected currency
  const subscriptionPrices = React.useMemo(
    () => catalog.filter((p) => p.recurring && p.recurring.interval === "week" && p.currency === currency),
    [catalog, currency]
  );

  // One-off prices (if any) for the selected currency
  const oneOffPrices = React.useMemo(
    () => catalog.filter((p) => !p.recurring && p.type === "one_time" && p.currency === currency),
    [catalog, currency]
  );

  // Auto-fallback: if current currency has no weekly subscriptions, jump to the first currency that does
  React.useEffect(() => {
    if (!catalog.length) return;
    const currencyWithWeekly = Array.from(
      new Set(
        catalog
          .filter((p) => p.recurring && p.recurring.interval === "week")
          .map((p) => p.currency)
      )
    );
    if (currencyWithWeekly.length === 0) return;
    if (!currencyWithWeekly.includes(currency)) {
      // Prefer customerCurrency if valid, else the first that has weekly
      const next = (customerCurrency && currencyWithWeekly.includes(customerCurrency))
        ? customerCurrency
        : currencyWithWeekly[0];
      setCurrency(next);
    }
  }, [catalog, currency, customerCurrency]);

  // Heuristic: pick three tiers by ascending price (or however many exist)
  const tieredSubs = React.useMemo(() => {
    const byProduct = new Map();
    subscriptionPrices.forEach((p) => {
      const key = p.productId;
      if (!byProduct.has(key)) byProduct.set(key, []);
      byProduct.get(key).push(p);
    });
    const pick = Array.from(byProduct.values()).map((arr) => arr[0]);
    return pick.sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0)).slice(0, 3);
  }, [subscriptionPrices]);

  const hasActiveSub = ["active", "trialing"].includes(activeSub?.status);

  const ensureUser = () => {
    const freshUser = auth.currentUser || user;
    if (!freshUser?.uid || !freshUser?.email) {
      appAlert("Please sign in first.").then(() => { window.location.href = "/host"; });
      return null;
    }
    return freshUser;
  };

  const subscribe = async (priceId, planName) => {
    try {
      const freshUser = ensureUser();
      if (!freshUser) return;

      if (hasActiveSub) {
        appAlert("You already have an active subscription. Use 'Update quantity' below or Manage Billing.");
        return;
      }

      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing/canceled`;

      const { url } = await createCheckoutSession({
        uid: freshUser.uid,
        email: freshUser.email,
        mode: "subscription",
        priceId,
        quantity: qty,
        success_url: successUrl,
        cancel_url: cancelUrl,
        metadata: { uid: freshUser.uid, email: freshUser.email, plan: planName, kind: "subscription" },
      });
      window.location.href = url;
    } catch (err) {
      console.error(err);
      appAlert(`Could not start checkout. ${String(err.message || err)}`);
    }
  };

  const oneOff = async (priceId, planName) => {
    try {
      const freshUser = ensureUser();
      if (!freshUser) return;

      const successUrl = `${window.location.origin}/billing/success`;
      const cancelUrl = `${window.location.origin}/billing/canceled`;

      const { url } = await createCheckoutSession({
        uid: freshUser.uid,
        email: freshUser.email,
        mode: "payment",
        priceId,
        quantity: 1,
        success_url: successUrl,
        cancel_url: cancelUrl,
        metadata: { uid: freshUser.uid, email: freshUser.email, plan: planName, kind: "oneoff", quantity: "1" },
      });
      window.location.href = url;
    } catch (err) {
      console.error(err);
      appAlert(`Could not start checkout. ${String(err.message || err)}`);
    }
  };

  const manageBilling = async () => {
    try {
      const freshUser = ensureUser();
      if (!freshUser) return;
      const { url } = await createPortalLink({
        uid: freshUser.uid,
        return_url: window.location.origin + "/pricing",
        // optional: configuration: "bpc_..." // you can pass it from the client too; server uses env if omitted
      });
      window.location.href = url;
    } catch (err) {
      console.error(err);
      appAlert(`Could not open billing portal. ${String(err.message || err)}`);
    }
  };

  const applyUpdateQuantity = async () => {
    try {
      const freshUser = ensureUser();
      if (!freshUser) return;
      const res = await updateSubscription({ uid: freshUser.uid, quantity: qty });
      appAlert(`Updated to ${res.quantity} quiz(es) per week. Changes take effect next cycle.`);
    } catch (e) {
      console.error(e);
      appAlert(`Could not update subscription. ${String(e.message || e)}`);
    }
  };

  const subscriptionCard = (p, label) => (
    <Plan
      key={p.priceId}
      title={label || p.productName || "Weekly Plan"}
      price={`${fmtMoney(p.unit_amount, p.currency)} / wk`}
      features={["Weekly subscription"]}
      quantity={qty}
      onQuantity={setQty}
      disabled={loadingAuth}
      highlight={false}
      onPrimary={() => subscribe(p.priceId, p.productName || "Weekly Plan")}
      primaryText={hasActiveSub ? "Already subscribed" : "Subscribe"}
      disabledPrimary={hasActiveSub}
    />
  );

  return (
    <div style={{ minHeight: "100vh", background: "#223b4c" }}>
      {/* Top bar stays slim; we removed the currency dropdown from here */}
      <div
        style={{
          width: "100vw",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>

        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>
          Pricing {loadingAuth ? "(checking sign-in…)" : user ? `— ${user.email}` : ""}
        </div>
      </div>

      {/* Sidebar */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      <div style={{ maxWidth: 1000, margin: "40px auto", padding: 16, color: "#f8faff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ margin: 0 }}>Pricing</h1>

          {/* Currency control lives in content; only show if >1 currency exists */}
          {catalogCurrencies.length > 1 && (
            <div style={{ marginLeft: "auto" }}>
              <label style={{ marginRight: 8, opacity: 0.9 }}>Currency:</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{ padding: 6, borderRadius: 8 }}
                aria-label="Select currency"
              >
                {catalogCurrencies.map((c) => (
                  <option key={c} value={c}>{c.toUpperCase()}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Current subscription summary (if any) */}
        {hasActiveSub && (
          <div
            style={{
              background: "rgba(32,30,48,0.58)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 12,
              padding: 16,
              marginTop: 16,
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Your current subscription</div>
            <div style={{ opacity: 0.9 }}>
              {activeSub.items?.[0]?.productName || "Weekly Plan"} —&nbsp;
              {fmtMoney(
                (subscriptionPrices.find((sp) => sp.priceId === activeSub.items?.[0]?.priceId)?.unit_amount) || 0,
                activeSub.items?.[0]?.currency || currency
              )}
              &nbsp;/ wk
            </div>
            <div style={{ marginTop: 8 }}>
              Quizzes per week:&nbsp;
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: 90, padding: 6, marginRight: 8 }}
              />
              <button onClick={applyUpdateQuantity}>Update quantity</button>
            </div>
            <div style={{ marginTop: 8 }}>
              <button onClick={manageBilling}>Open Billing Portal</button>
            </div>
          </div>
        )}

        {/* Subscription plans from Stripe */}
        <section style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", marginTop: 20 }}>
          {tieredSubs.length === 0 && (
            <div style={{ opacity: 0.9 }}>
              No weekly subscription prices are configured for {currency.toUpperCase()}.
            </div>
          )}
          {tieredSubs.map((p) => subscriptionCard(p, "Weekly"))}
        </section>

        <h3 style={{ marginTop: 32 }}>One-off quiz night</h3>
        <section style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))" }}>
          {oneOffPrices.map((p) => (
            <Card
              key={p.priceId}
              title={p.productName || "One-Off Event"}
              price={fmtMoney(p.unit_amount, p.currency)}
              features={["1 event credit"]}
              onPrimary={() => oneOff(p.priceId, p.productName || "One-Off Event")}
              disabled={loadingAuth}
            />
          ))}
        </section>

        <div style={{ marginTop: 24 }}>
          <button onClick={manageBilling} disabled={loadingAuth}>
            Manage Billing
          </button>
        </div>
      </div>
    </div>
  );
}

function Plan({ title, price, features, quantity, onQuantity, onPrimary, highlight, disabled, primaryText = "Subscribe", disabledPrimary = false }) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 12,
        padding: 16,
        background: "rgba(32,30,48,0.58)",
        boxShadow: highlight ? "0 0 0 2px #4f46e5 inset" : "none",
        color: "#f8faff",
        opacity: disabled ? 0.8 : 1,
      }}
    >
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <div style={{ fontSize: 22, fontWeight: 600, margin: "6px 0" }}>{price}</div>
      <p style={{ marginTop: 0 }}>Select quizzes per week:</p>
      <input
        type="number"
        min={1}
        value={quantity}
        onChange={(e) => onQuantity(Math.max(1, Number(e.target.value) || 1))}
        style={{ width: 90, padding: 6, marginBottom: 8 }}
        disabled={disabled}
      />
      <ul style={{ marginTop: 8 }}>{features.map((f) => <li key={f}>{f}</li>)}</ul>
      <button onClick={onPrimary} style={{ marginTop: 8 }} disabled={disabled || disabledPrimary}>
        {primaryText}
      </button>
    </div>
  );
}

function Card({ title, price, features, onPrimary, disabled }) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 12,
        padding: 16,
        background: "rgba(32,30,48,0.58)",
        color: "#f8faff",
        opacity: disabled ? 0.8 : 1,
      }}
    >
      <h4 style={{ marginTop: 0 }}>{title}</h4>
      <div style={{ fontSize: 22, fontWeight: 600, margin: "6px 0" }}>{price}</div>
      <ul style={{ marginTop: 8 }}>{features.map((f) => <li key={f}>{f}</li>)}</ul>
      <button onClick={onPrimary} style={{ marginTop: 8 }} disabled={disabled}>
        Buy
      </button>
    </div>
  );
}
